import React from 'react';
import classNames from 'classnames';

import Option from './Option';
import Helpers from '../helpers';

import { ProductFormContext } from '../../../../context/product-form-context';

const ns = `product-option-group`;

const OptionGroupAsSwatches = ({
	media,
	option,
	index: groupIndex,
	setFilter,
	setFilters,
	filters,
}) => {
	//	--------
	//	Mount detection: detect when this component is mounted so processes do not continue.
	//	--------
	const mounted = React.useRef(false);

	React.useEffect(() => {
		//	Gets set to true on component mount.
		mounted.current = true;
		//	Gets set to false on component unmount by React.
		return () => { mounted.current = false; };
	}, []);

	const {
		availabilityMap,
		product,
		showErrors,
	} = React.useContext(ProductFormContext);

	const allOptionValues =	option.values;

	const availableOptionValues = Helpers.getAvailableOptionsWithinSelections(
		product,
		groupIndex,
		filters,
		availabilityMap
	);

	//	----------
	//	Pre-select OptionGroups containing only one 'available' option
	// (which is based on 'filters' have already been applied)
	//	----------
	React.useEffect(() => {
		const thisOptionGroupAlreadyHasAFilter = !!filters[groupIndex];
		if (
			mounted.current && !thisOptionGroupAlreadyHasAFilter
			&& availableOptionValues.length
		) {
			setFilter(groupIndex, availableOptionValues[0]);
		}
	}, [mounted.current, filters, groupIndex]); // eslint-disable-line

	//	---------------
	//	Generate Swatch Image Map
	//	---------------
	const swatchMap = {};

	media.forEach(({
		mediaContentType,
		image,
	}) => {
		if (!image?.altText || mediaContentType !== 'IMAGE') return;
		const splitAltText = image?.altText?.split(':');
		const isSwatchImage = splitAltText[0]
			.trim()
			.toLocaleLowerCase() === 'swatch';

		if (isSwatchImage) {
			const key = splitAltText[1].trim().toLocaleLowerCase();
			const value = image.src;

			swatchMap[key] = value;
		}
	});

	//	---------------
	//	Render Children
	//	---------------
	const options = allOptionValues.map(_v => {
		const selected = filters[groupIndex] === _v; // eslint-disable-line
		const doesExist = Helpers.doesOptionExistWithinSelections(
			product,
			groupIndex,
			_v,
			filters
		);
		const isInStock = Helpers.isOptionInStockWithinSelections(
			product,
			groupIndex,
			_v,
			filters,
			availabilityMap
		);

		return (
			<Option
				groupIndex={groupIndex}
				key={_v}
				value={_v}
				doesExist={doesExist}
				isInStock={isInStock}
				selected={selected}
				setFilter={setFilter}
				setFilters={setFilters}
				swatchMap={swatchMap}
			/>
		);
	})
		.filter(_o => { return !!_o; });	//	Clean null values from array so .length is accurate

	//	Don't render anything if there are no options within this Option Group.
	if (!options.length)	return null;

	const selectedLabel	= filters[groupIndex];

	const rootClassnames = classNames({
		[`${ ns }`]: true,
		[`${ ns }--swatches`]: true,
		[`${ ns }--error`]: !selectedLabel && showErrors,
	});

	return (
		<div
			className={rootClassnames}
			data-index={groupIndex}
			data-name={option.name.trim()}
			data-handle={option.name.trim().toLowerCase()}
		>
			<div className={`${ ns }__label`}>
				<span className={`${ ns }__name`}>
					{`${ option.name } `}
				</span>
				<span className={`${ ns }__selected`}>
					{selectedLabel}
				</span>
			</div>
			<div className={`${ ns }__options`}>
				{options}
			</div>
			{!selectedLabel && showErrors && (
				<div className={`${ ns }__error`}>
					{`Please select a ${ option.name.toLowerCase() }`}
				</div>
			)}
		</div>
	);
};

export default OptionGroupAsSwatches;
